import {assistantsTransform} from "../assistants/AssistantsTransform";
import {ManagementTransformBase} from "../TranformBase";

import {
  CallTransferRecordModel,
  CallTransferListModel,
  TransferCallActionStatusModel,
  TransferCallActionBackgroundMusicModel,
  TransferCallActionBackgroundMusicTypeModel,
  TransferCallActionAdvancedModel,
  AvailabilityModel,
  TransferCallActionWarmCallTransferModel,
  DayAvailabilityModel,
  WeekDays,
  CallTransferUpdateModel,
  CallTransferCreateModel,
  TransferCallActionWarmCallTransferMode,
  CallTransferModeModel,
} from "@/api/models";
import {
  PaginatedResponse,
  TransferCallAction,
  TransferCallActionAdvanced,
  TransferCallActionBackgroundMusic,
  TransferCallActionBackgroundMusicType,
  TransferCallActionHumanAvailability,
  TransferCallActionHumanAvailabilityScheduleItem,
  TransferCallActionHumanAvailabilityScheduleItemWeekday,
  TransferCallActionStatus,
  TransferCallActionType,
  TransferCallActionCallTransferInfoVoicemailDetection,
  TransferCallActionCallTransferInfoMode,
  TransferCallActionCallTransferInfo,
} from "@/api/services";
import {EditCallTransferSchemaFormData} from "@/utils/callTransfer/schema";
import {BaseAction} from "@/types/api";

export const availabilityItemDefaultValue = {
  isAvailable: false,
  start: "",
  end: "",
};

export class ActionCallTransferTransform extends ManagementTransformBase<CallTransferRecordModel> {
  public makeBackgroundMusicModel(
    dto: TransferCallActionBackgroundMusic,
  ): TransferCallActionBackgroundMusicModel {
    const model: TransferCallActionBackgroundMusicModel = {
      type: dto?.type as TransferCallActionBackgroundMusicTypeModel,
      url: dto?.url,
      message: dto?.message,
    };

    return model;
  }

  public makeHumanAvailabilityScheduleItemModel(
    dto: TransferCallActionHumanAvailabilityScheduleItem,
  ): DayAvailabilityModel {
    const {weekday, interval} = dto;

    const isAvailable = !!weekday && !!interval;
    const [start, end] = (interval || "").split("-");

    const model: DayAvailabilityModel = {
      start,
      end,
      isAvailable,
    };

    return model;
  }

  public makeHumanAvailabilityModel(
    dto: TransferCallActionHumanAvailability,
  ): AvailabilityModel {
    const model: AvailabilityModel = {
      monday: availabilityItemDefaultValue,
      tuesday: availabilityItemDefaultValue,
      wednesday: availabilityItemDefaultValue,
      thursday: availabilityItemDefaultValue,
      friday: availabilityItemDefaultValue,
      saturday: availabilityItemDefaultValue,
      sunday: availabilityItemDefaultValue,
    };

    dto?.schedule?.forEach(item => {
      if (item?.weekday) {
        const day = item.weekday.toLowerCase() as WeekDays;
        const itemModel = this.makeHumanAvailabilityScheduleItemModel(item);
        model[day] = itemModel;
      }
    });

    return model;
  }

  public makeAdvancedModel(
    dto: TransferCallActionAdvanced,
  ): TransferCallActionAdvancedModel {
    const model: TransferCallActionAdvancedModel = {
      extension: dto?.extension,
      timeout: dto?.timeout,
      tryingMessage: dto?.trying_message,
      answeredMessage: dto?.answered_message,
      noAnswerMessage: dto?.no_answer_message,
    };

    return model;
  }

  public makeWarmCallTransferModel(
    dto: TransferCallActionCallTransferInfo,
  ): TransferCallActionWarmCallTransferModel {
    const model: TransferCallActionWarmCallTransferModel = {
      voicemailDetection:
        dto?.voicemail_detection as TransferCallActionCallTransferInfoVoicemailDetection,
      language: dto?.language,
      message: dto?.message,
      preMessagePause: dto?.pre_message_pause,
      postMessagePause: dto?.post_message_pause,
      mode: dto?.mode as TransferCallActionWarmCallTransferMode,
    };

    return model;
  }

  public makeRecordModel(
    dto: TransferCallAction & BaseAction & {phone_book_name?: string},
  ): CallTransferRecordModel {
    const transferTargetType =
      dto.transfer_target_type as CallTransferModeModel;
    const phoneBookName = dto.phone_book_name;

    const transferToDisplay =
      transferTargetType === CallTransferModeModel.PHONE_BOOK
        ? phoneBookName || dto.phone_number
        : dto.phone_number;

    const model: CallTransferRecordModel = {
      ...dto,
      // TODO: add mode here once BE supports it
      title: dto.name,
      description: "",
      transferToPhone: transferToDisplay as string,
      conditionTrigger: dto.trigger,
      assistants: dto.assistants.map(assistant =>
        assistantsTransform.makeEvenCompactModel(assistant),
      ),
      transferTargetType: dto.transfer_target_type as CallTransferModeModel,
      phoneBookId: dto?.phone_book_id,
      name: dto.name,
      type: dto.type,
      isHumanAvailableEnabled: dto.human_availability?.active,
      humanNotAvailableMessage:
        // INFO: the default value shoult be set on the BE
        dto.human_availability?.unavailable_message ||
        "There is no-one available to take your call at the moment. Please try again later.",
      workspaceId: dto.workspace_id,
      status: (dto.status ||
        TransferCallActionStatus.active) as TransferCallActionStatusModel,
      ...(dto.phone_number && {phoneNumber: dto.phone_number}),
      ...(dto.background_music?.active && {
        backgroundMusic: this.makeBackgroundMusicModel(dto.background_music),
      }),
      ...(dto.advanced && {advanced: this.makeAdvancedModel(dto.advanced)}),
      availability: this.makeHumanAvailabilityModel(dto.human_availability),
      timezone: dto.human_availability?.timezone,
      ...(dto.call_transfer_info && {
        callTransferInfo: this.makeWarmCallTransferModel(
          dto.call_transfer_info,
        ),
      }),
    };

    return model;
  }

  public makeListModel(dto: PaginatedResponse): CallTransferListModel {
    const model: CallTransferListModel = {
      records: dto.items.map(item =>
        this.makeRecordModel(item as TransferCallAction & BaseAction),
      ),
      pagination: this.makePaginationModel(dto),
    };

    return model;
  }

  public makeHumanAvailabilityDto(
    model?: AvailabilityModel,
    timezone?: string,
    humanNotAvailableMessage?: string,
    active?: boolean,
  ): TransferCallActionHumanAvailability {
    const schedule = [];
    Object.keys(model || {}).forEach(itemKey => {
      const weekday = this.makeWeekdayDto(itemKey as WeekDays);

      const availabilityItem = model?.[itemKey as WeekDays] || {};

      if (availabilityItem?.isAvailable) {
        schedule.push({
          weekday:
            weekday as unknown as TransferCallActionHumanAvailabilityScheduleItemWeekday,
          interval: availabilityItem.start + "-" + availabilityItem.end,
        });
      }
    });

    const dto: TransferCallActionHumanAvailability = {
      schedule: active ? schedule : [],
      ...(timezone && {timezone}),
      ...(humanNotAvailableMessage && {
        unavailable_message: humanNotAvailableMessage,
      }),
      active,
    };
    return dto;
  }

  public makeWeekdayDto(model: WeekDays): string {
    return model.charAt(0).toUpperCase() + model.slice(1);
  }

  public makeTransferCallActionBaseDto(
    model: EditCallTransferSchemaFormData & {
      workspaceId: string;
    },
  ): TransferCallAction {
    const dto: TransferCallAction = {
      workspace_id: model?.workspaceId,
      type: TransferCallActionType.transfer,
    };

    if ("conditionTrigger" in model) {
      dto.trigger = model.conditionTrigger;
    }

    if ("transferToPhone" in model) {
      dto.phone_number = model.transferToPhone;
      dto.name = model.transferToPhone;
    }

    if ("phoneBookId" in model) {
      dto.phone_book_id = model.phoneBookId;
    }
    dto.transfer_target_type = model.mode;

    // Background Music
    dto.background_music = {
      active: model.isBackgroundMusicEnabled,
      url: model?.customAudioUrl,
      type: model?.backgroundMusic
        ?.name as TransferCallActionBackgroundMusicType,
      message: model?.initialMessage,
    };

    // Advanced Settings
    if (
      [
        "extension",
        "timeout",
        "reachTargetMessage",
        "answerMessage",
        "didntAnswerMessage",
      ].some(key => key in model)
    )
      dto.advanced = {
        ...(model?.extension ? {extension: model?.extension} : {}),
        ...(model?.timeout ? {timeout: Number(model?.timeout)} : {}),
        ...(model?.reachTargetMessage
          ? {trying_message: model?.reachTargetMessage}
          : {}),
        ...(model?.answerMessage
          ? {answered_message: model?.answerMessage}
          : {}),
        ...(model?.didntAnswerMessage
          ? {no_answer_message: model?.didntAnswerMessage}
          : {}),
      };

    // Human Availability
    dto.human_availability = this.makeHumanAvailabilityDto(
      model?.availability,
      model.timezone,
      model?.humanNotAvailableMessage,
      model.isHumanAvailableEnabled,
    );

    // Warm Call Transfer
    if (
      [
        "voiceMailDetection",
        "languageCode",
        "callTransferInfo.mode",
        "callTransferInfo.message",
        "callTransferInfo.preMessagePause",
        "callTransferInfo.postMessagePause",
      ].some(key => key in model)
    )
      dto.call_transfer_info = {
        voicemail_detection:
          model.voiceMailDetection as TransferCallActionCallTransferInfoVoicemailDetection,
        language: model.languageCode,
        message: model?.callTransferInfo?.message,
        mode: model?.callTransferInfo
          ?.mode as TransferCallActionCallTransferInfoMode,
        ...(model?.callTransferInfo?.preMessagePause
          ? {pre_message_pause: Number(model.callTransferInfo.preMessagePause)}
          : {}),
        ...(model?.callTransferInfo?.postMessagePause
          ? {
              post_message_pause: Number(
                model.callTransferInfo.postMessagePause,
              ),
            }
          : {}),
      };

    return dto;
  }

  public makeCreateDto(
    model: CallTransferCreateModel & {
      workspaceId: string;
    },
  ): TransferCallAction {
    const baseDto = this.makeTransferCallActionBaseDto(model);
    baseDto.status = TransferCallActionStatus.active;
    return baseDto;
  }

  public makeUpdateDto(model: CallTransferUpdateModel): TransferCallAction {
    const baseDto = this.makeTransferCallActionBaseDto(model);
    return {
      ...baseDto,
      slug: model.id,
    };
  }
}

export const actionCallTransferTransform = new ActionCallTransferTransform();
